
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    InputAdornment,
    Snackbar,
    Stack,
    TextField,
    Tooltip,
    Typography, Autocomplete,
} from '@mui/material';

import {
    Add as AddIcon,
    Delete as DeleteIcon,
    Edit as EditIcon,
  //  Refresh as RefreshIcon,
    Search as SearchIcon,
} from '@mui/icons-material';

import { DataGrid } from '@mui/x-data-grid';

import {
    getRejectionLevels,
    createRejectionLevel,
    updateRejectionLevel,
    deleteRejectionLevel,
} from '../../services/rejectionlevel';
import { getAuthToken } from '../../utils/token';
import config from '../../config/config';
//import { red } from '@mui/material/colors';



const initialForm = {
    product_id: '',
    quantity_rejected: '',
    quantity_accepted: '',
    rejection_date: new Date().toISOString().slice(0, 10),
    rejection_level: '',
    remark: '',
};

const getErrorMessage = (error) =>
    error?.response?.data?.message ||
        error?.response?.data?.errors
        ? error.response?.data?.message ||
        Object.values(error.response?.data?.errors || {})
            .flat()
            .join(' ')
        : error?.message || 'Something went wrong.';

const normalizeRows = (response) => {
    const payload = response?.data ?? response;

    if (Array.isArray(payload)) return payload;
    if (Array.isArray(payload?.data)) return payload.data;
    if (Array.isArray(payload?.data?.data)) return payload.data.data;

    return [];
};
const rejectionLevelOptions = [
    { value: 1, label: 'Level 3' },
    { value: 2, label: 'Level 4' },
    { value: 3, label: 'Level 5' },
    { value: 3, label: 'Level 6' },
];

export default function RejectionLevel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [form, setForm] = useState(initialForm);
    const [search, setSearch] = useState('');
    const [errors, setErrors] = useState({});
    const [notification, setNotification] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [productOptions, setProductOptions] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);

    const showNotification = (message, severity = 'success') => {
        setNotification({ open: true, message, severity });
    };
    const fetchProductList = useCallback(async () => {
        const token = getAuthToken();

        if (!token) {
            showNotification('Authentication token not found.', 'error');
            return;
        }

        setProductsLoading(true);

        try {
            const response = await axios.get(
                `${config.API}/product/list`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: 'application/json',
                    },
                }
            );

            setProductOptions(
                response?.data?.products?.map((product) => ({
                    value: product.id,
                    label: product.name,
                })) || []
            );
        } catch (err) {
            console.error('Error fetching products:', err);

            showNotification(
                err.response?.data?.message ||
                'Failed to load products.',
                'error'
            );
        } finally {
            setProductsLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchProductList();
    }, [fetchProductList]);

    const loadRows = useCallback(async () => {
        setLoading(true);

        try {
            const response = await getRejectionLevels({
                search: search.trim() || undefined,
            });

            setRows(normalizeRows(response));
        } catch (error) {
            showNotification(getErrorMessage(error), 'error');
        } finally {
            setLoading(false);
        }
    }, [search]);

    useEffect(() => {
        loadRows();
    }, [loadRows]);

    const openCreateDialog = () => {
        setEditingId(null);
        setForm({
            ...initialForm,
            rejection_date: new Date().toISOString().slice(0, 10),
        });
        setErrors({});
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditingId(row.id);
        setForm({
            product_id: row.product_id ?? '',
            quantity_rejected: row.quantity_rejected ?? '',
            quantity_accepted: row.quantity_accepted ?? '',
            rejection_date: String(row.rejection_date ?? '').slice(0, 10),
            rejection_level: row.rejection_level ?? '',
            remark: row.remark ?? '',
        });
        setErrors({});
        setDialogOpen(true);
    };

    const closeDialog = () => {
        if (saving) return;
        setDialogOpen(false);
        setEditingId(null);
        setErrors({});
    };

    const handleChange = (event) => {
        const { name, value } = event.target;

        setForm((previous) => ({
            ...previous,
            [name]: value,
        }));

        setErrors((previous) => ({
            ...previous,
            [name]: '',
        }));
    };

    const validateForm = () => {
        const nextErrors = {};

        if (
            form.product_id === '' ||
            !Number.isInteger(Number(form.product_id)) ||
            Number(form.product_id) < 1
        ) {
            nextErrors.product_id = 'Please select a valid product.';
        }

        if (
            form.quantity_rejected === '' ||
            !Number.isFinite(Number(form.quantity_rejected)) ||
            Number(form.quantity_rejected) <= 0
        ) {
            nextErrors.quantity_rejected =
                'Rejected quantity must be greater than zero.';
        }

        if (
            form.quantity_accepted === '' ||
            !Number.isFinite(Number(form.quantity_accepted)) ||
            Number(form.quantity_accepted) <= 0
        ) {
            nextErrors.quantity_accepted =
                'Accepted quantity must be greater than zero.';
        }

        if (!form.rejection_date) {
            nextErrors.rejection_date = 'Rejection date is required.';
        }

        if (![1, 2, 3,4].includes(Number(form.rejection_level))) {
            nextErrors.rejection_level = 'Please select a rejection level.';
        }

        // Correct remark validation
        if (!String(form.remark ?? '').trim()) {
            nextErrors.remark = 'Please enter a remark.';
        }

        setErrors(nextErrors);

        return Object.keys(nextErrors).length === 0;
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!validateForm()) return;

        const payload = {
            product_id: Number(form.product_id),
            quantity_rejected: Number(form.quantity_rejected),
            quantity_accepted: Number(form.quantity_accepted),
            rejection_date: form.rejection_date,
            rejection_level: String(form.rejection_level),
            remark: String(form.remark ?? '').trim(),
        };

        setSaving(true);

        try {
            if (editingId !== null) {
                await updateRejectionLevel(editingId, payload);
                showNotification('Rejection record updated successfully.');
            } else {
                await createRejectionLevel(payload);
                showNotification('Rejection record created successfully.');
            }

            setDialogOpen(false);
            setEditingId(null);
            await loadRows();
        } catch (error) {
            const serverErrors = error?.response?.data?.errors;

            if (serverErrors) {
                const mappedErrors = {};

                Object.entries(serverErrors).forEach(([key, messages]) => {
                    mappedErrors[key] = Array.isArray(messages)
                        ? messages[0]
                        : String(messages);
                });

                setErrors(mappedErrors);
            }

            showNotification(getErrorMessage(error), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (row) => {
        const confirmed = window.confirm(
            `Delete rejection record #${row.id}?`
        );

        if (!confirmed) return;

        try {
            await deleteRejectionLevel(row.id);
            showNotification('Rejection record deleted successfully.');
            await loadRows();
        } catch (error) {
            showNotification(getErrorMessage(error), 'error');
        }
    };

    const columns = useMemo(
        () => [
            {
                field: 'id',
                headerName: 'ID',
                width: 50,
                type: 'number',
            },
            {
                field: 'product_name',
                headerName: 'Item',
                minWidth: 200,
                flex: 0.8,
              //  type: 'number',
            },
            {
                field: 'quantity_accepted',
                headerName: 'Inspected Qty',
                minWidth: 100,
                flex: 1,
                type: 'number',
                valueFormatter: (value) =>
                    Number(value ?? 0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                    }),
            },
            {
                field: 'quantity_rejected',
                headerName: 'Rejected Qty',
                minWidth: 100,
                flex: 1,
                type: 'number',
                valueFormatter: (value) =>
                    Number(value ?? 0).toLocaleString(undefined, {
                        maximumFractionDigits: 2,
                    }),
            },
            {
                field: 'rejection_date',
                headerName: 'Rejection Date',
                minWidth: 110,
                flex: 1,
                valueFormatter: (value) =>
                    value ? String(value).slice(0, 10) : '',
            },
            {
                field: 'rejection_level',
                headerName: 'Rejected Level',
                minWidth: 100,
                flex: 1,
                valueFormatter: (value) => {
                    const option = rejectionLevelOptions.find(
                        (item) => Number(item.value) === Number(value)
                    );

                    return option?.label || '';
                },
            },

            {
                field: 'remark',
                headerName: 'Remark',
                minWidth: 100,
                flex: 1,

            },
            {
                field: 'actions',
                headerName: 'Actions',
                width: 100,
                sortable: false,
                filterable: false,
                disableColumnMenu: true,
                renderCell: (params) => (
                    <Stack direction="row" spacing={0.5}>
                        <Tooltip title="Edit">
                            <IconButton
                                size="small"
                                color="primary"
                                onClick={() => openEditDialog(params.row)}
                            >
                                <EditIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>

                        <Tooltip title="Delete">
                            <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDelete(params.row)}
                            >
                                <DeleteIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                ),
            },
        ],
        []
    );

    return (
        <Box sx={{ p: { xs: 1, sm: 1 }, width: '100%' }}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                justifyContent="space-between"
                spacing={1.5}
                sx={{ mb: 1 }}
            >
                <Box>
                    <Typography variant="h5" fontWeight={700}>
                        Rejection
                    </Typography>

                </Box>

                <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                >
                  

                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={openCreateDialog}
                        size="small"
                        sx={{
                            minWidth: 120,
                            height: 35,
                            textTransform: 'none',
                        }}
                    >
                        Add Rejection
                    </Button>
                    <TextField
                        size="small"
                        placeholder="Search records..."
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        sx={{ minWidth: { sm: 230 } }}
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                    />


                </Stack>
            </Stack>

            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderRadius: 2,
                }}
            >
                <DataGrid
                    rows={rows}
                    columns={columns}
                    getRowId={(row) => row.id}
                    loading={loading}
                    autoHeight
                    disableRowSelectionOnClick
                    pageSizeOptions={[5, 10, 25, 50, 100]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                page: 0,
                                pageSize: 10,
                            },
                        },
                    }}
                    density="compact"
                    sx={{
                        border: 1,
                        borderColor: 'divider',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: 'action.hover',
                            fontWeight: 700,
                        },
                        '& .MuiDataGrid-cell': {
                            display: 'flex',
                            alignItems: 'center',
                        },
                    }}
                    slots={{
                        loadingOverlay: () => (
                            <Box
                                sx={{
                                    height: '100%',
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <CircularProgress size={28} />
                            </Box>
                        ),
                    }}
                />
            </Box>

            <Dialog
                open={dialogOpen}
                onClose={closeDialog}
                fullWidth
                maxWidth="sm"
            >
                <Box component="form" onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingId !== null
                            ? 'Edit Rejection Record'
                            : 'Add Rejection Record'}
                    </DialogTitle>

                    <DialogContent dividers>
                        <Stack spacing={1} sx={{ pt: 1 }}>
                            <Autocomplete
                                options={productOptions}
                                loading={productsLoading}
                                loadingText="Loading products..."
                                value={
                                    productOptions.find(
                                        (product) =>
                                            String(product.value) === String(form.product_id)
                                    ) || null
                                }
                                onChange={(_, selectedProduct) => {
                                    setForm((previous) => ({
                                        ...previous,
                                        product_id: selectedProduct?.value ?? '',
                                    }));

                                    setErrors((previous) => ({
                                        ...previous,
                                        product_id: '',
                                    }));
                                }}
                                getOptionLabel={(option) => option?.label || ''}
                                isOptionEqualToValue={(option, value) =>
                                    String(option.value) === String(value.value)
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Product"
                                        size="small"
                                        fullWidth
                                        required
                                        error={Boolean(errors.product_id)}
                                        helperText={errors.product_id}
                                        placeholder="Search product..."
                                    />
                                )}
                            />
                            <TextField
                                name="quantity_accepted"
                                label="Inspected Qty"
                                type="number"
                                size="small"
                                fullWidth
                                required
                                value={form.quantity_accepted}
                                onChange={handleChange}
                                error={Boolean(errors.quantity_accepted)}
                                helperText={errors.quantity_accepted}
                                slotProps={{
                                    htmlInput: { min: 0.01, step: 0.01 },
                                }}
                            />

                            <TextField
                                name="quantity_rejected"
                                label="Rejected Quantity"
                                type="number"
                                size="small"
                                fullWidth
                                required
                                value={form.quantity_rejected}
                                onChange={handleChange}
                                error={Boolean(errors.quantity_rejected)}
                                helperText={errors.quantity_rejected}
                                slotProps={{
                                    htmlInput: { min: 0.01, step: 0.01 },
                                }}
                            />

                            <TextField
                                name="rejection_date"
                                label="Rejection Date"
                                type="date"
                                size="small"
                                fullWidth
                                required
                                value={form.rejection_date}
                                onChange={handleChange}
                                error={Boolean(errors.rejection_date)}
                                helperText={errors.rejection_date}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />

                            <Autocomplete
                                options={rejectionLevelOptions}
                                value={
                                    rejectionLevelOptions.find(
                                        (option) =>
                                            String(option.value) === String(form.rejection_level)
                                    ) || null
                                }
                                onChange={(_, selectedOption) => {
                                    setForm((previous) => ({
                                        ...previous,
                                        rejection_level: selectedOption
                                            ? selectedOption.value
                                            : '',
                                    }));

                                    setErrors((previous) => ({
                                        ...previous,
                                        rejection_level: '',
                                    }));
                                }}
                                getOptionLabel={(option) => option.label}
                                isOptionEqualToValue={(option, value) =>
                                    option.value === value.value
                                }
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Rejection Level"
                                        size="small"
                                        fullWidth
                                        required
                                        error={Boolean(errors.rejection_level)}
                                        helperText={errors.rejection_level}
                                        placeholder="Select rejection level"
                                    />
                                )}
                            />
                            <TextField
                                name="remark"
                                label="Remark"
                                type="text"
                                size="small"
                                fullWidth
                                required
                                value={form.remark}
                                onChange={handleChange}
                                error={Boolean(errors.remark)}
                                helperText={errors.remark}
                                slotProps={{
                                    inputLabel: { shrink: true },
                                }}
                            />
                        </Stack>
                    </DialogContent>

                    <DialogActions sx={{ p: 2 }}>
                        <Button
                            onClick={closeDialog}
                            disabled={saving}
                            color="inherit"
                        >
                            Cancel
                        </Button>

                        <Button
                            type="submit"
                            variant="contained"
                            disabled={saving}
                            startIcon={
                                saving ? (
                                    <CircularProgress size={16} />
                                ) : undefined
                            }
                        >
                            {saving
                                ? 'Saving...'
                                : editingId !== null
                                    ? 'Update'
                                    : 'Save'}
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>

            <Snackbar
                open={notification.open}
                autoHideDuration={4000}
                onClose={() =>
                    setNotification((previous) => ({
                        ...previous,
                        open: false,
                    }))
                }
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
            >
                <Alert
                    severity={notification.severity}
                    variant="filled"
                    onClose={() =>
                        setNotification((previous) => ({
                            ...previous,
                            open: false,
                        }))
                    }
                >
                    {notification.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}
 /* <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadRows}
                        disabled={loading}
                        size="small"
                        sx={{
                            minWidth: 120,
                            height: 35,
                            textTransform: 'none',
                        }}
                    >
                        Refresh
                    </Button>*/